// Selector de voz para el modo "Video con influencer" (V2).
//
// Desde ADR-550 la fuente es el generador de voz de GEMINI (tabla
// `voces_google`): se manda el NOMBRE de la voz como `voz_id` y NO existe
// clonado de voz en este proveedor (el clonado ElevenLabs sigue viviendo en
// el Director de Estudio). Mismo chrome que `GanchoPicker` (portal: bottom
// sheet en mobile, panel lateral derecho en desktop):
//   • SIN opción "Automática": la voz es OBLIGATORIA (ADR-544) — el padre
//     valida `vozId != null` antes de generar y preselecciona la última voz
//     usada.
//   • Subtítulo "Voces de Atlas" + filtros de género (etiqueta editorial de
//     la tabla) + las voces (nombre + estilo), con preview reproducible
//     cuando la fila tiene `preview_url`.
//
// OJO clases: los colores brand-* NO soportan modificador de opacidad (el
// config los define como var() sin <alpha-value> — la clase no compila).
// Para tintes usar hex arbitrario: `bg-[#1644EB]/[0.06]`, `border-[#1644EB]/40`.

import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { Check, Loader2, Pause, Play, X } from 'lucide-react';
import { useExitTransition } from '../../../hooks/useExitTransition';
import { fetchGoogleVoices, type GoogleVoice } from '../googleVoicesApi';

export interface VoicePickerV2Props {
  open: boolean;
  onClose: () => void;
  /** Voz elegida: NOMBRE de la voz Gemini (`voces_google.nombre`).
   *  null = todavía no eligió (la voz es obligatoria, ADR-544). */
  selectedVozId: string | null;
  /** `nombre` viaja junto al id para mostrarlo en la fila "Voz" del panel.
   *  Con Gemini id y nombre son el mismo string (ADR-550). */
  onSelect: (vozId: string, nombre: string) => void;
}

const SECTION_TITLE_CLASS =
  'text-[11px] uppercase tracking-wide font-medium text-[#3C3C43]/55';

export const VoicePickerV2: React.FC<VoicePickerV2Props> = ({
  open,
  onClose,
  selectedVozId,
  onSelect,
}) => {
  const { mounted, animated } = useExitTransition(open, 300);
  const [voices, setVoices] = useState<GoogleVoice[]>([]);
  const [loading, setLoading] = useState(false);
  const [sexFilter, setSexFilter] = useState<string>('Todas');
  const fetchedRef = useRef(false);

  // Preview de voz: un solo <audio> compartido; `playingId` marca cuál suena.
  const previewRef = useRef<HTMLAudioElement | null>(null);
  const [playingId, setPlayingId] = useState<string | null>(null);

  // Fetch lazy en la primera apertura.
  useEffect(() => {
    if (!open || fetchedRef.current) return;
    fetchedRef.current = true;
    setLoading(true);
    void fetchGoogleVoices()
      .then((rows) => {
        setVoices(rows);
        setLoading(false);
      })
      .catch((err) => {
        // Sin catch, un reject dejaba el spinner colgado para siempre y
        // `fetchedRef` en true impedía reintentar al reabrir. Reseteamos el
        // flag para que la próxima apertura vuelva a intentar el fetch.
        console.error('[VoicePickerV2] fetch de voces falló', err);
        fetchedRef.current = false;
        setLoading(false);
      });
  }, [open]);

  const stopPreview = () => {
    if (previewRef.current) {
      previewRef.current.pause();
      previewRef.current = null;
    }
    setPlayingId(null);
  };

  // Al cerrar el panel, cortar el preview.
  useEffect(() => {
    if (!open) stopPreview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const togglePreview = (voice: GoogleVoice) => {
    if (!voice.previewUrl) return;
    if (playingId === voice.id) {
      stopPreview();
      return;
    }
    stopPreview();
    const audio = new Audio(voice.previewUrl);
    previewRef.current = audio;
    setPlayingId(voice.id);
    audio.onended = () => setPlayingId(null);
    void audio.play().catch(() => setPlayingId(null));
  };

  const pick = (voice: GoogleVoice) => {
    stopPreview();
    onSelect(voice.nombre, voice.nombre);
    onClose();
  };

  if (!mounted) return null;

  const sexOptions = [
    'Todas',
    ...Array.from(
      new Set(voices.map((v) => v.sexo).filter((s): s is string => !!s)),
    ).sort(),
  ];
  const filteredVoices =
    sexFilter === 'Todas' ? voices : voices.filter((v) => v.sexo === sexFilter);

  return ReactDOM.createPortal(
    <>
      <div
        className={`fixed inset-0 z-[210] bg-black/40 transition-opacity duration-300 ${
          animated ? 'opacity-100' : 'opacity-0'
        }`}
        onClick={onClose}
      />
      <div
        className={`fixed z-[211] bg-white flex flex-col shadow-2xl transition-transform duration-300 ease-out
          inset-x-0 bottom-0 rounded-t-[13px] max-h-[calc(100dvh-1rem-env(safe-area-inset-top,0px))]
          md:inset-x-auto md:left-auto md:top-4 md:right-4 md:bottom-4 md:h-auto md:w-[480px] md:rounded-[13px]
          ${
            animated
              ? 'translate-y-0 md:translate-x-0'
              : 'translate-y-full md:translate-y-0 md:translate-x-[calc(100%+32px)]'
          }`}
        style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 16px)' }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 md:py-4 shrink-0">
          <h3 className="text-[16px] md:text-[17px] font-semibold text-brand-black md:flex-1">
            Voz
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 inline-flex items-center justify-center rounded-full bg-black/[0.06] text-[#3C3C43]/40 hover:bg-black/[0.1] transition-colors"
            aria-label="Cerrar"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="mx-5 h-px bg-black/[0.08] shrink-0" />

        {/* Lista de voces */}
        {loading ? (
          <div className="flex-1 flex items-center justify-center py-12">
            <Loader2 className="w-6 h-6 text-brand-primary animate-spin" />
          </div>
        ) : (
          <div className="flex-1 min-h-0 overflow-y-auto px-5 py-4 flex flex-col gap-2">
            <p className={SECTION_TITLE_CLASS}>Voces de Atlas</p>
            {sexOptions.length > 2 && (
              <div className="flex gap-1.5 flex-wrap">
                {sexOptions.map((opt) => (
                  <button
                    key={opt}
                    type="button"
                    onClick={() => setSexFilter(opt)}
                    className={`px-3 py-1.5 rounded-full text-[12.5px] font-medium transition-colors ${
                      sexFilter === opt
                        ? 'bg-brand-primary text-white'
                        : 'bg-black/[0.05] text-[#3C3C43]/60 hover:bg-black/[0.08]'
                    }`}
                  >
                    {opt}
                  </button>
                ))}
              </div>
            )}
            {filteredVoices.length === 0 ? (
              <p className="text-[13.5px] text-[#3C3C43]/50 text-center py-10">
                No pudimos cargar las voces. Probá de nuevo en un rato.
              </p>
            ) : (
              filteredVoices.map((voice) => {
                const isSelected = selectedVozId === voice.nombre;
                const isPlaying = playingId === voice.id;
                return (
                  <div
                    key={voice.id}
                    className={`w-full rounded-2xl p-3.5 transition-colors flex items-center gap-3 ${
                      isSelected
                        ? 'bg-[#1644EB]/[0.06] ring-2 ring-brand-primary'
                        : 'bg-white ring-1 ring-black/[0.08] hover:bg-black/[0.02]'
                    }`}
                  >
                    <button
                      type="button"
                      onClick={() => pick(voice)}
                      className="flex-1 min-w-0 text-left"
                    >
                      <div className="flex items-center gap-2">
                        <span className="text-[14px] font-medium text-brand-black truncate">
                          {voice.nombre}
                        </span>
                        {isSelected && (
                          <Check className="w-3.5 h-3.5 text-brand-primary shrink-0" />
                        )}
                      </div>
                      <p className="text-[12px] text-[#3C3C43]/50 mt-0.5 truncate">
                        {[voice.sexo, voice.estilo].filter(Boolean).join(' · ') ||
                          voice.descripcion ||
                          'Voz'}
                      </p>
                    </button>
                    {voice.previewUrl && (
                      <button
                        type="button"
                        onClick={() => togglePreview(voice)}
                        className={`w-9 h-9 rounded-full inline-flex items-center justify-center shrink-0 transition-colors ${
                          isPlaying
                            ? 'bg-brand-primary text-white'
                            : 'bg-black/[0.05] text-[#3C3C43]/60 hover:bg-black/[0.1]'
                        }`}
                        aria-label={
                          isPlaying ? 'Pausar preview' : 'Escuchar preview'
                        }
                      >
                        {isPlaying ? (
                          <Pause className="w-4 h-4" />
                        ) : (
                          <Play className="w-4 h-4 ml-0.5" />
                        )}
                      </button>
                    )}
                  </div>
                );
              })
            )}
          </div>
        )}
      </div>
    </>,
    document.body,
  );
};
